import { animate, style, transition, trigger } from '@angular/animations';

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  output,
  signal,
  Signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { deleteObject, getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import Dexie from 'dexie';
import * as moment from 'moment';
import { catchError, firstValueFrom, map, skip, Subscription, switchMap, take, tap } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { EnvService } from 'src/app/env.service';
import { AiRestrictedHoverDirective } from 'src/app/shared/directives/ai-restricted-hover.directive';
import { ConfigurationService } from 'src/app/shared/services/configuration.service';
import { TenantService } from 'src/app/shared/services/tenant/tenant.service';
import { UploadService } from 'src/app/shared/services/upload/upload.service';
import { StorageService } from 'src/app/storage.service';
import { ChunkService, CreateChunkResponse } from 'src/app/views/content/chunk/chunk.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { ContentUtilsService } from 'src/app/views/content/content-utils.service';
import { ModelsService } from 'src/app/views/my-space/my-models/models.service';
import { SocketsService } from 'src/app/views/sockets.service';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { audioBufferToBlob, base64ToFile, blobToBase64, blobToFile, checkAudioStreamActivity, fileToBase64, getDurationFromFile } from '../../../helpers/audio.helper';
import { AppConfirmService } from '../../../services/app-confirm/app-confirm.service';
import { DynamicOverlayService } from '../../../services/dynamic-overlay/dynamic-overlay.service';
import { ToastService } from '../../../services/toast/toast.service';
import { AudioPlayerComponent } from '../../audio-player/audio-player.component';
import { CommandType, TranscriptionOptionsComponent } from '../../audio-player/transcription-options.component';
import { NotEnoughCreditComponent } from '../../snackbar/not-enough-credit/not-enough-credit.component';
import { CustomContextMenuItem } from '../../ui/custom-context-menu/custom-context-menu.component';
import { LiveTranscriptionPayload, TranscriptionModels, TranscriptionPayload } from './audio-recorder.dto';
import { LiveWaveformDisplayComponent } from './live-waveform-display/live-waveform-display.component';
import { Command } from './options/audio-menus.dto';
import { AudioRecorderMenuComponent } from './options/audio-recorder-menu/audio-recorder-menu.component';
import { AudioRecordingOptionsComponent } from './options/audio-recording-options.component';
import { RecordingIndicatorComponent } from './recording-indicator/recording-indicator.component';
import { TranscriptionProgressIndicatorComponent } from './transcription-progress-indicator/transcription-progress-indicator.component';

// This is a browser global, but declaring it helps with TypeScript linting.
declare let MediaRecorder: any;

class FailsafeDB extends Dexie {
  chunks!: Dexie.Table<{ sessionId: string; chunkIndex: number; data: Blob }, [string, number]>;
  orphanedFiles!: Dexie.Table<{ url: string; contentId: string }, string>;
  constructor() {
    super('FailsafeDB');
    this.version(3).stores({
      chunks: '[sessionId+chunkIndex], sessionId',
      orphanedFiles: 'url, contentId',
    });
  }
}
const failsafeDb = new FailsafeDB();

@Component({
  selector: 'app-audio-recorder',
  templateUrl: './audio-recorder.component.html',
  styleUrls: ['../recorders.scss', './audio-recorder.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslateModule,
    AudioPlayerComponent,
    LiveWaveformDisplayComponent,
    TranscriptionProgressIndicatorComponent,
    RecordingIndicatorComponent,
    AiRestrictedHoverDirective,
  ],
  animations: [
    trigger('growShrink', [
      transition(':enter', [style({ transform: 'scaleX(0)', 'transform-origin': 'left' }), animate('500ms ease-out', style({ transform: 'scaleX(1)' }))]),
      // transition(':leave', [style({ 'transform-origin': 'left' }), animate('100ms ease-in', style({ transform: 'scaleX(0)' }))]),
    ]),
  ],
})
export class AudioRecorderComponent implements AfterViewInit, OnDestroy, OnChanges {
  contentID = input.required<string>();
  audioRecordingDeleted = output<void>();
  private recordingSessionId: string = '';
  private chunkIndex: number = 0;
  private accumulatedBlobs: Blob[] = [];
  private isResuming: boolean = false;
  @Input() params: any;
  @Input() showAudioButton: boolean = false;
  @Input() origin: string;
  @Input() showTranscribeButton: boolean = false;
  @Input() compact: boolean = false;
  @Input() hideWaveform: boolean = false;
  @Input() continueTranscriptionMode: boolean = false;
  @Input() currentText: string = '';
  enableRealTimeCloudRecording = false; // Set to true to enable streaming chunks to cloud in real-time

  @ViewChild('audioRecording', { static: false }) audioRecordingElement: ElementRef<HTMLAudioElement>;
  @ViewChild('audioPlayerRef') audioPlayerComponent!: AudioPlayerComponent;

  userConfig;
  transcriptionSettings;
  generativeAISettings;
  generativeTemplateData;
  templateData;
  mediaRecorder: any;
  recordedBlobs: Blob[] = [];
  currentAudioFile: File | null = null;
  loading: boolean = false;
  loadings = {
    recorder: false,
    fileUploader: false,
    transcriber: {
      live: false,
      recorded: false,
    },
  };
  audioDurationBeingFiguredOut: boolean = false;
  audioRecordingStartTime: moment.Moment;
  savedAudioFile: File | null = null;
  audioFeed: MediaStream | null = null;
  selectedMicrophone: string;
  get liveAudioStream() {
    return this.contentStateService.liveAudioStream();
  }
  updatingAudioSource: boolean = false;
  private liveTranscriptionDuration = { seconds: 0 };
  private currentRecordedBlobs: Blob[] = [];
  private finalTranscription = '';
  private interimTranscription = '';
  private isFirstSegmentOfSession: boolean = true;
  private transcriptionAudioContext: AudioContext | null = null;
  private transcriptionWorkletNode: any = null;
  private transcriptionMicSource: any = null;

  private confirm = inject(AppConfirmService);
  private translate = inject(TranslateService);
  private dynamicOverlayService = inject(DynamicOverlayService);
  private socketService = inject(SocketsService);
  private chunkService = inject(ChunkService);
  private toast = inject(ToastService);
  private authService = inject(AuthService);
  private uploadService = inject(UploadService);
  private modelService = inject(ModelsService);
  private env = inject(EnvService);
  private storageService = inject(StorageService);
  private contentUtilsService = inject(ContentUtilsService);
  public contentStateService = inject(ContentStateService);
  public configService = inject(ConfigurationService);
  private tenantService = inject(TenantService);
  private readonly destroyRef = inject(DestroyRef);
  private storage = inject(Storage);
  resolvedAudioUrl = signal<string>('');
  private liveTranscriptionChunkId: string | null = null;

  private userConfiguration: Subscription;
  private socketSubscription: Subscription;
  private TTSFileSubscription: Subscription;
  private menuSubscription: Subscription;
  private audioRecordingOptionsSub: Subscription;
  private transcriptionOptionsSub: Subscription;
  private chunkCreatedSubscription: Subscription;

  isRecording = signal(false);
  isMenuOpen = signal(false);
  latestRecordAction = signal<'record-audio' | 'record-with-transcription' | 'transcribe-only'>('record-audio');
  isRecordingAndTranscribing = false;

  private cdr = inject(ChangeDetectorRef);

  base64AudioData: Signal<{ data: any; duration: number } | undefined> = toSignal(
    toObservable(this.contentID).pipe(
      switchMap((id) => {
        if (!id) {
          return [];
        }
        return this.storageService.find$('contentsCurrentAudio', [id]);
      }),
      map((res) => (res && res.length > 0 ? res[0] : undefined)),
      tap(() => {
        this.updatingAudioSource = false;
      })
    )
  );

  canUseAI = this.authService.canUseAI();

  optionButtons: CustomContextMenuItem[] = [
    {
      id: 'generate-with-ai',
      text: 'AudioPlayer.Action.GenerateWithAI',
      icon: 'auto_awesome',
      func: () => this.generateContentFromAudio(),
      disabled: !this.authService.canUseAI(),
    },
    {
      id: 'transcribe-recorded-audio',
      text: 'Courses.ChunkCreator.TranscribeSavedAudio',
      icon: 'speech_to_text',
      func: () => this.transcribeAudio(),
      disabled: !this.authService.canUseAI(),
    },
    { id: 'insert-into-content', text: 'AudioPlayer.Action.InsertAudioIntoContent', icon: 'arrow_insert', func: () => this.uploadAudioAsChunk() },
    { id: 'delete', text: 'AudioPlayer.Action.Delete', icon: 'delete', func: () => this.deleteAudioRecording() },
  ];

  private objectUrlsToRevoke: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentText'] && this.contentStateService.isLiveTranscribing() && this.contentStateService.liveTranscriptionSource() === 'recorder') {
      this.syncFinalTranscriptionFromHtml(this.currentText);
    }
  }

  private syncFinalTranscriptionFromHtml(html: string): void {
    if (!html) {
      this.finalTranscription = '';
      return;
    }
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Remove interim (italicized) elements
    const italicElements = tempDiv.querySelectorAll('i');
    italicElements.forEach((i) => i.remove());

    const paragraphs = tempDiv.querySelectorAll('p');
    if (paragraphs.length > 0) {
      this.finalTranscription = Array.from(paragraphs)
        .map((p) => (p.textContent || p.innerText || '').trim())
        .join('\n');
    } else {
      this.finalTranscription = (tempDiv.textContent || tempDiv.innerText || '').trim();
    }
  }

  constructor() {
    effect(() => {
      const audioData = this.base64AudioData()?.data;
      if (audioData) {
        if (this.currentAudioFile) {
          this.resolvedAudioUrl.set(this.createSafeObjectURL(this.currentAudioFile));
          return;
        }

        if (typeof audioData === 'string' && (audioData.startsWith('http') || audioData.startsWith('gs://'))) {
          this.getDownloadURL(audioData)
            .then((url) => {
              this.resolvedAudioUrl.set(url);
            })
            .catch(() => {
              this.resolvedAudioUrl.set(audioData);
            });
        } else if (audioData instanceof Blob) {
          const url = this.createSafeObjectURL(audioData);
          this.resolvedAudioUrl.set(url);
        } else {
          this.resolvedAudioUrl.set(audioData as any);
        }
      } else {
        this.resolvedAudioUrl.set('');
      }
    });

    this.configService
      .getConfigValue('transcriptionSettings')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((settings) => {
        // if (settings?.model) {
        //   this.transcriptionSettings.model = settings?.model;
        // } else {
        //   this.transcriptionSettings.model = 'chirp3';
        // }
        this.transcriptionSettings = { model: 'chirp3' };
      });

    const savedAction = localStorage.getItem('latestRecordAction');
    if (savedAction === 'record-audio') {
      this.latestRecordAction.set(savedAction as any);
    } else if ((savedAction === 'record-with-transcription' || savedAction === 'transcribe-only') && this.canUseAI) {
      this.latestRecordAction.set(savedAction as any);
    } else {
      this.latestRecordAction.set('record-audio');
    }

    this.selectedMicrophone = localStorage.getItem('selectedMicrophone') || '';

    this.userConfiguration = this.authService.userConfiguration$.subscribe((res) => {
      this.selectedMicrophone = localStorage.getItem('selectedMicrophone') || '';
      if (res.transcriptionSettings) {
        this.transcriptionSettings = res.transcriptionSettings;
        if (this.transcriptionSettings.template && this.transcriptionSettings.template != 'none') {
          this.modelService.getTemplate(this.transcriptionSettings.template).subscribe((res) => {
            this.templateData = res.data || '';
          });
        } else {
          this.templateData = '';
        }
      }
      if (res.generativeAISettings) {
        this.generativeAISettings = res.generativeAISettings;
        if (this.generativeAISettings.template && this.generativeAISettings.template != 'none') {
          this.modelService.getTemplate(this.generativeAISettings.template).subscribe((res) => {
            this.generativeTemplateData = res.data || '';
          });
        } else {
          this.generativeTemplateData = '';
        }
      }
    });

    this.subscribeToSocketEvents();

    this.contentStateService.stopLiveTranscriptionCommand$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // Only stop if we are actively transcribing from this recorder
      if (this.contentStateService.isLiveTranscribing() && this.contentStateService.liveTranscriptionSource() === 'recorder') {
        this.stopLiveTranscription();
      }
    });

    this.chunkCreatedSubscription = this.chunkService.chunkCreatedOnCache$.subscribe((chunk: any) => {
      if (chunk && this.contentStateService.isLiveTranscribing()) {
        this.chunkService.setChunkToFocusOn(chunk._id);
      }
    });

    this.TTSFileSubscription = this.chunkService.chunkTTSFileAction$.pipe(skip(1)).subscribe(async (blob: any) => {
      const audioFile = blobToFile(blob, 'TTS.mp3');
      const base64data = (await blobToBase64(audioFile)) as string;
      const duration = await getDurationFromFile(audioFile);

      await this.storageService.store('contentsCurrentAudio', {
        _id: this.contentID(),
        data: base64data.replace('data:application/octet-stream;', 'data:audio/mpeg;'),
        duration: duration,
      });
    });
  }

  ngAfterViewInit() {
    // FIX: Defer the async operation to the next change detection cycle.
    setTimeout(() => {
      // Load any previously saved audio when the component is ready
      this.storageService.find('contentsCurrentAudio', [this.contentID()]).then((res: Array<any>) => {
        if (res.length > 0) {
          const audioData = res[0].data;
          if (audioData instanceof Blob) {
            const url = this.createSafeObjectURL(audioData);
            if (this.audioRecordingElement) {
              this.audioRecordingElement.nativeElement.src = url;
            }
            this.recordedBlobs = [audioData];
            this.currentAudioFile = blobToFile(audioData, 'recording.webm');
            return;
          }

          if (typeof audioData === 'string' && (audioData.startsWith('http') || audioData.startsWith('gs://'))) {
            this.getDownloadURL(audioData).then((url) => {
              if (this.audioRecordingElement) {
                this.audioRecordingElement.nativeElement.src = url;
              }
            });
            this.currentAudioFile = null;
            return;
          }

          if (typeof audioData === 'string') {
            const byteCharacters = atob(audioData.split(',')[1]);
            const byteArrays = [];
            for (let offset = 0; offset < byteCharacters.length; offset += 512) {
              const slice = byteCharacters.slice(offset, offset + 512);
              const byteNumbers = new Array(slice.length);
              for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              byteArrays.push(byteArray);
            }
            this.recordedBlobs = byteArrays;

            const mimeType = res[0].data.match(/:(.*?);/)?.[1] || 'audio/webm';
            const ext = mimeType === 'audio/wav' ? 'wav' : mimeType === 'audio/mpeg' || mimeType === 'audio/mp3' ? 'mp3' : 'webm';
            const superBuffer = new Blob(this.recordedBlobs, { type: mimeType });
            this.currentAudioFile = blobToFile(superBuffer, `recording.${ext}`);
            if (this.audioRecordingElement) {
              this.audioRecordingElement.nativeElement.src = res[0].data;
            }
          }
        }
      });
    }, 0);
  }

  subscribeToSocketEvents(): void {
    this.socketSubscription = this.socketService.streamEvent$.subscribe((event) => {
      switch (event.type) {
        case 'end-call':
          this.chunkService
            .finalizeLiveTranscription({
              model: this.transcriptionSettings.model,
              ...this.liveTranscriptionDuration,
            })
            .subscribe({
              next: (res) => {},
              error: (err) => console.error(err),
            });
          break;
        case 'call-transcription-data':
          this.liveTranscriptionDuration.seconds = Number(event.data?.raw?.totalBilledTime?.seconds || 0);
          break;
        case 'curate-transcription-error':
          this.liveTranscriptionDuration = { seconds: 0 };
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
            duration: 2000,
          });
          this.loading = false;
          this.mediaRecorder?.stop();
          this.audioFeed?.getTracks().forEach((track) => {
            track.stop();
          });
          this.chunkService
            .finalizeLiveTranscription({
              model: this.transcriptionSettings.model,
              ...this.liveTranscriptionDuration,
            })
            .subscribe({
              next: (res) => {},
              error: (err) => console.error(err),
            });
          break;
        case 'curate-transcription-done':
          console.log('curate-transcription-done ...');

          this.mediaRecorder?.stop();
          this.audioFeed?.getTracks().forEach((track) => {
            track.stop();
          });
          this.loading = false;
          const geminiData = event.data?.raw;
          if (!this.liveTranscriptionDuration.seconds || !geminiData) {
            break;
          }
          this.chunkService
            .finalizeLiveTranscription({
              model: this.transcriptionSettings.model,
              ...this.liveTranscriptionDuration,
              geminiData,
            })
            .subscribe({
              next: (res) => {},
              error: (err) => console.error(err),
            });
          this.liveTranscriptionDuration = { seconds: 0 };
          break;
        case 'transcription-interim-data':
          if (this.contentStateService.liveTranscriptionSource() === 'recorder' && event.data.contentID === this.contentID()) {
            this.interimTranscription = (event.data.text || '').trim();
            const livePreview = this.getTranscriptionHtml(this.finalTranscription, this.interimTranscription);
            this.chunkService.passLiveTranscriptionData(livePreview, false);
          }
          break;
        case 'transcription-data':
          if (this.contentStateService.liveTranscriptionSource() === 'recorder') {
            this.liveTranscriptionDuration.seconds = Number(event.data?.raw?.totalBilledTime?.seconds || 0);
            if (event.data.contentID === this.contentID()) {
              const newText = (event.data.text || '').trim();
              if (newText) {
                let separator = ' ';
                if (this.finalTranscription) {
                  if (this.isFirstSegmentOfSession) {
                    separator = '\n';
                    this.isFirstSegmentOfSession = false;
                  }
                } else {
                  this.isFirstSegmentOfSession = false;
                }

                this.finalTranscription = this.finalTranscription ? `${this.finalTranscription}${separator}${newText}` : newText;
              }
              this.interimTranscription = '';

              const chunkId = this.contentStateService.highlightedChunkId();
              if (chunkId) {
                const chunks = this.contentStateService.content();
                const targetChunk = chunks.find((c: any) => c._id === chunkId);
                if (targetChunk) {
                  const edition = targetChunk.editions?.find((e: any) => e.language === this.translate.currentLang) || targetChunk.editions?.[0];
                  if (edition) {
                    this.chunkService
                      .editChunk({
                        id: chunkId,
                        edition: edition,
                        type: 'canvas-transcript',
                        language: this.translate.currentLang,
                        content: this.getTranscriptionHtml(this.finalTranscription),
                        contentID: this.contentStateService.contentId,
                      })
                      .subscribe();
                  }
                }
              }

              this.chunkService.passLiveTranscriptionData(this.getTranscriptionHtml(this.finalTranscription), true);
            }
          }
          break;
        case 'stopped-stream':
          if (this.contentStateService.liveTranscriptionSource() === 'recorder') {
            this.stopLiveTranscription();
          }
          break;
        case 'stream-end':
          if (this.contentStateService.liveTranscriptionSource() === 'recorder') {
            this.stopLiveTranscription();
          }
          break;
        case 'disconnected':
          if (this.contentStateService.isLiveTranscribing() && this.contentStateService.liveTranscriptionSource() === 'recorder') {
            this.stopLiveTranscription();
            this.toast.snackbar.open(this.translate.instant('Snackbar.TranscriptionConnectionError'), 'Ok', {
              duration: 5000,
            });
          }
          break;
      }
    });
  }

  openAudioRecordingOptions(event: MouseEvent, triggerElement: HTMLElement): void {
    event.stopPropagation();
    this.audioRecordingOptionsSub?.unsubscribe();
    const { ref: overlayRef, componentInstance } = this.dynamicOverlayService.open(triggerElement, AudioRecordingOptionsComponent, { data: {} });
    this.audioRecordingOptionsSub = componentInstance.commandSelected.subscribe((command: Command) => {
      const action = this.latestRecordAction();
      switch (command.type) {
        case 'restart-recording':
          if (action === 'record-audio') {
            this.startRecordingAudio(false);
          } else {
            if (!this.canUseAI) return;
            this.startLiveTranscription(action === 'record-with-transcription');
          }
          break;
        case 'continue-recording':
          if (action === 'record-audio') {
            this.resumeRecordingAudio();
          } else {
            this.resumeLiveTranscription();
          }
          break;
      }
      this.dynamicOverlayService.close();
    });
    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => this.audioRecordingOptionsSub?.unsubscribe());
  }

  openSplitButtonMenu(event: MouseEvent, triggerElement: HTMLElement): void {
    event.stopPropagation();
    this.isMenuOpen.set(true);
    this.menuSubscription?.unsubscribe();
    const { ref: overlayRef, componentInstance } = this.dynamicOverlayService.open(triggerElement, AudioRecorderMenuComponent, {
      data: {
        hasRecording: !!this.base64AudioData(),
        isRecording: this.isRecording(),
        isTranscribing: this.contentStateService.isLiveTranscribing() && this.contentStateService.liveTranscriptionSource() === 'recorder',
      },
    });

    this.menuSubscription = componentInstance.commandSelected.subscribe((command: Command) => {
      this.dynamicOverlayService.close();
      this.handleMenuCommand(command, event, triggerElement);
    });

    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => {
        this.menuSubscription?.unsubscribe();
        this.isMenuOpen.set(false);
      });
  }

  handleMenuCommand(command: Command, event: MouseEvent, triggerElement?: HTMLElement): void {
    if (command.type === 'record-audio' || command.type === 'record-with-transcription' || command.type === 'transcribe-only') {
      this.latestRecordAction.set(command.type);
      localStorage.setItem('latestRecordAction', command.type);
    }

    switch (command.type) {
      case 'record-audio':
      case 'record-with-transcription':
        if (this.base64AudioData()) {
          this.openAudioRecordingOptions(event, triggerElement);
        } else {
          if (command.type === 'record-audio') {
            this.startRecordingAudio(false);
          } else {
            if (!this.canUseAI) return;
            this.startLiveTranscription();
          }
        }
        break;
      case 'transcribe-only':
        if (!this.canUseAI) return;
        this.startLiveTranscription(false);
        break;
      case 'add-audio-file':
        this.uploadRecording();
        break;
      case 'restart-recording':
        this.startRecordingAudio(false);
        break;
      case 'continue-recording':
        this.resumeRecordingAudio();
        break;
      case 'transcribe-recorded-audio':
        this.transcribeAudio();
        break;
    }
  }

  executeMainAction(event: MouseEvent, trigger: HTMLElement): void {
    const action = this.latestRecordAction();
    if (this.base64AudioData() && action !== 'transcribe-only') {
      this.openAudioRecordingOptions(event, trigger);
    } else {
      if (action === 'record-audio') {
        this.startRecordingAudio();
      } else if (action === 'record-with-transcription') {
        if (!this.canUseAI) return;
        this.startLiveTranscription();
      } else {
        if (!this.canUseAI) return;
        this.startLiveTranscription(false);
      }
    }
  }

  async allowedToReplaceAudio() {
    const savedRecordedAudio: any = await this.storageService.find('contentsCurrentAudio', [this.contentID()]);
    if (savedRecordedAudio.length) {
      const response = await firstValueFrom(
        this.confirm.confirm({
          title: this.translate.instant('Snackbar.Warning'),
          message: this.translate.instant('Snackbar.ReplaceAudioRecordingWarning'),
        })
      );
      return response;
    }
    return true;
  }

  async startRecordingAudio(warnBeforeStarting = true) {
    if (warnBeforeStarting) {
      if (!(await this.allowedToReplaceAudio())) {
        return;
      }
    }

    // We always want a clean state for MediaRecorder chunks, even when resuming,
    // because we will stitch them properly using AudioContext in processFinishedRecording.
    this.currentRecordedBlobs = [];
    this.recordingSessionId = uuidv4();
    this.chunkIndex = 0;
    this.isResuming = false;

    try {
      let audioConstraints: any = { echoCancellation: true };
      const savedMic = localStorage.getItem('selectedMicrophone') || this.selectedMicrophone;
      if (savedMic && savedMic !== 'default') {
        audioConstraints.deviceId = { exact: savedMic };
      }

      let media: MediaStream;
      try {
        media = await (navigator.mediaDevices as any).getUserMedia({ audio: audioConstraints });
      } catch (err) {
        console.warn('[AudioRecorder] Failed exact microphone, falling back to ideal/default:', err);
        audioConstraints = { echoCancellation: true };
        if (savedMic && savedMic !== 'default') {
          audioConstraints.deviceId = { ideal: savedMic };
        }
        media = await (navigator.mediaDevices as any).getUserMedia({ audio: audioConstraints });
      }

      checkAudioStreamActivity(media).then((hasActivity) => {
        if (!hasActivity) {
          this.toast.showAudioTroubleshootingDialog();
        }
      });

      this.audioFeed = media;
      this.contentStateService.liveAudioStream.set(media);
      media.getTracks().forEach((track) => {
        track.onended = () => {
          this.stopRecordingAudio();
          this.isRecording.set(false);
        };
      });

      this.mediaRecorder = new MediaRecorder(media, { mimeType: 'audio/webm' });

      this.mediaRecorder.ondataavailable = (event: any) => {
        if (event.data && event.data.size > 0) {
          this.handleIncomingChunk(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.processFinishedRecording();
      };

      this.mediaRecorder.start(5000);
      this.isRecording.set(true);
      this.audioRecordingStartTime = moment();
    } catch (err: any) {
      console.error('Error starting recording:', err);
      if (err.name === 'NotAllowedError' || err.message?.includes('Permission denied')) {
        this.toast.showDevicePermissionError();
      } else {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'An error occurred', 'OK', { duration: 3000 });
      }
    }
  }

  private async processFinishedRecording() {
    const duration = this.audioRecordingStartTime ? moment().diff(this.audioRecordingStartTime, 'seconds') : 0;
    const sessionId = this.recordingSessionId;
    const chunkId = this.liveTranscriptionChunkId;
    this.liveTranscriptionChunkId = null;

    this.loading = true;
    this.cdr.detectChanges();

    let chunkBlobs: Blob[] = [];
    if (this.currentRecordedBlobs.length > 0) {
      chunkBlobs = [...this.currentRecordedBlobs];
    } else {
      try {
        const dbChunks = await failsafeDb.chunks.where('sessionId').equals(sessionId).sortBy('chunkIndex');
        chunkBlobs = dbChunks.map((c) => c.data);
      } catch (err) {
        console.error('Error fetching chunks from failsafeDb', err);
      }
    }

    const mimeType = chunkBlobs.length > 0 ? chunkBlobs[0].type || 'audio/webm' : 'audio/webm';
    const ext = mimeType === 'audio/wav' ? 'wav' : mimeType === 'audio/mpeg' || mimeType === 'audio/mp3' ? 'mp3' : 'webm';
    const newSegmentBlob = new Blob(chunkBlobs, { type: mimeType });

    if (this.savedAudioFile) {
      await this._concatenateAndSaveRecordings(this.savedAudioFile, newSegmentBlob);
      this.savedAudioFile = null;

      if (this.enableRealTimeCloudRecording) {
        // Finalize the backend chunk stream to clean up temp files, but ignore its fileLink result
        // since we use the local concatenated base64 for fallback and upload.
        this.chunkService
          .finalizeAudioChunkStream({
            sessionId,
            duration,
            contentID: this.contentID(),
            chunkId,
            language: this.translate.currentLang,
            transcribe: false,
            settings: { model: this.transcriptionSettings.model || 'chirp3' },
          })
          .subscribe({
            next: async (res: any) => {
              if (res.fileLink) {
                const gsUri = res.fileLink.startsWith('http') ? res.fileLink.replace('https://storage.googleapis.com/', 'gs://') : res.fileLink;
                await failsafeDb.orphanedFiles.put({ url: gsUri, contentId: this.contentID() });
              }
            },
          });
      } else {
        this.loading = false;
        await failsafeDb.chunks.where('sessionId').equals(sessionId).delete();
        this.cdr.detectChanges();
      }
      return;
    }

    const localUrl = this.createSafeObjectURL(newSegmentBlob);
    if (this.audioRecordingElement) {
      this.audioRecordingElement.nativeElement.src = localUrl;
    }
    this.currentAudioFile = blobToFile(newSegmentBlob, `recording.${ext}`);

    // Optimistically store stitched local blob immediately for offline/failsafe playback
    try {
      const base64Audio = await blobToBase64(newSegmentBlob);
      await this.storageService.store('contentsCurrentAudio', {
        _id: this.contentID(),
        data: base64Audio,
        duration: duration,
      });
    } catch (err) {
      console.error('Error storing base64 audio fallback', err);
    }
    this.cdr.detectChanges();

    if (this.enableRealTimeCloudRecording) {
      this.chunkService
        .finalizeAudioChunkStream({
          sessionId,
          duration,
          contentID: this.contentID(),
          chunkId,
          language: this.translate.currentLang,
          transcribe: false,
          settings: { model: this.transcriptionSettings.model || 'chirp3' },
        })
        .subscribe({
          next: async (res: any) => {
            this.loading = false;
            await failsafeDb.chunks.where('sessionId').equals(sessionId).delete();

            const fileLink = res.fileLink;
            const gsUri = fileLink.startsWith('http') ? fileLink.replace('https://storage.googleapis.com/', 'gs://') : fileLink;

            await failsafeDb.orphanedFiles.put({ url: gsUri, contentId: this.contentID() });

            const signedUrl = await this.getDownloadURL(gsUri);
            if (this.audioRecordingElement) {
              this.audioRecordingElement.nativeElement.src = signedUrl;
            }

            await this.storageService.store('contentsCurrentAudio', {
              _id: this.contentID(),
              data: gsUri,
              duration: duration,
            });

            this.toast.snackbar.open(this.translate.instant('Snackbar.UploadComplete') || 'Upload complete', 'Ok', { duration: 3000 });
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.loading = false;
            // finalize stream failed (e.g. offline). Keep the optimistically stored localBlob.
            this.toast.snackbar.open(this.translate.instant('Snackbar.UploadFailedLocalCopy') || 'Upload failed, saved local copy.', 'Ok', { duration: 3000 });
            this.cdr.detectChanges();
          },
        });
    } else {
      this.loading = false;
      await failsafeDb.chunks.where('sessionId').equals(sessionId).delete();
      this.cdr.detectChanges();
    }
  }

  /**
   * Takes two audio blobs, concatenates them, and saves the result in contentsCurrentAudio.
   */
  private async _concatenateAndSaveRecordings(savedBlob: Blob, newBlob: Blob) {
    const context = new AudioContext();
    try {
      this.loading = true;
      this.cdr.detectChanges();

      const savedBuffer = await context.decodeAudioData(await savedBlob.arrayBuffer());
      const newBuffer = await context.decodeAudioData(await newBlob.arrayBuffer());

      const outputBuffer = context.createBuffer(1, savedBuffer.length + newBuffer.length, savedBuffer.sampleRate);

      outputBuffer.getChannelData(0).set(savedBuffer.getChannelData(0), 0);
      outputBuffer.getChannelData(0).set(newBuffer.getChannelData(0), savedBuffer.length);

      const finalBlob = audioBufferToBlob(outputBuffer);
      this.currentAudioFile = blobToFile(finalBlob, 'recording.wav');

      const localUrl = this.createSafeObjectURL(finalBlob);
      if (this.audioRecordingElement) {
        this.audioRecordingElement.nativeElement.src = localUrl;
      }

      const base64Audio = await blobToBase64(finalBlob);
      const combinedDuration = savedBuffer.duration + newBuffer.duration;

      await this.storageService.store('contentsCurrentAudio', {
        _id: this.contentID(),
        data: base64Audio,
        duration: combinedDuration,
      });

      this.loading = false;
      this.cdr.detectChanges();
    } catch (err) {
      console.error('Failed to concatenate audio files:', err);
      this.loading = false;
      this.cdr.detectChanges();
    } finally {
      if (context.state !== 'closed') {
        context.close();
      }
    }
  }

  async resumeRecordingAudio() {
    this.isResuming = true;
    try {
      const audioDataInfo = this.base64AudioData();
      if (audioDataInfo && audioDataInfo.data) {
        const audioData = audioDataInfo.data;
        if (typeof audioData === 'string' && audioData.startsWith('data:')) {
          this.savedAudioFile = base64ToFile(audioData, 'previous.webm');
        } else if (audioData instanceof Blob) {
          this.savedAudioFile = blobToFile(audioData, 'previous.webm');
        } else if (this.currentAudioFile) {
          this.savedAudioFile = this.currentAudioFile;
        } else {
          const url = this.resolvedAudioUrl();
          if (url) {
            const response = await fetch(url);
            this.savedAudioFile = blobToFile(await response.blob(), 'previous.webm');
          }
        }
      }
    } catch (err) {
      console.error('Could not fetch previous audio for concatenation', err);
    }
    this.startRecordingAudio(false);
  }

  async resumeLiveTranscription() {
    this.isResuming = true;
    try {
      const audioDataInfo = this.base64AudioData();
      if (audioDataInfo && audioDataInfo.data) {
        const audioData = audioDataInfo.data;
        if (typeof audioData === 'string' && audioData.startsWith('data:')) {
          this.savedAudioFile = base64ToFile(audioData, 'previous.webm');
        } else if (audioData instanceof Blob) {
          this.savedAudioFile = blobToFile(audioData, 'previous.webm');
        } else if (this.currentAudioFile) {
          this.savedAudioFile = this.currentAudioFile;
        } else {
          const url = this.resolvedAudioUrl();
          if (url) {
            const response = await fetch(url);
            this.savedAudioFile = blobToFile(await response.blob(), 'previous.webm');
          }
        }
      }
    } catch (err) {
      console.error('Could not fetch previous audio for concatenation', err);
    }
    this.startLiveTranscription(this.latestRecordAction() === 'record-with-transcription');
  }

  onData(event: any) {
    if (event.data && event.data.size > 0) {
      this.handleIncomingChunk(event.data);
    }
  }

  stopRecordingAudio() {
    if (this.audioFeed) {
      this.audioFeed.getTracks().forEach((track) => track.stop());
      this.audioFeed = null;
    }
    this.contentStateService.liveAudioStream.set(null);
    this.isRecording.set(false);

    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
  }

  async handleIncomingChunk(blob: Blob) {
    const sessionId = this.recordingSessionId;
    const index = this.chunkIndex++;

    this.currentRecordedBlobs.push(blob);

    try {
      await failsafeDb.chunks.put({ sessionId, chunkIndex: index, data: blob });
    } catch (err) {
      console.error(`Failed to save chunk ${index} to failsafeDb`, err);
    }

    // NOTE: Real-time cloud uploading is disabled to keep recording fully local.
    // If needed in the future, uncomment this block to stream chunks to the cloud.
    /*
    this.chunkService.uploadAudioChunkStream(sessionId, index, blob).subscribe({
      next: () => console.log(`Chunk ${index} uploaded successfully`),
      error: (err) => {
        console.error(`Failed to upload chunk ${index}`, err);
      }
    });
    */
  }

  handleLiveTranscriptionRecordingChunk(blob: Blob) {
    this.accumulatedBlobs.push(blob);
    if (this.accumulatedBlobs.length >= 10) {
      const combinedBlob = new Blob(this.accumulatedBlobs, { type: 'audio/webm' });
      this.handleIncomingChunk(combinedBlob);
      this.accumulatedBlobs = [];
    }
  }

  async flushAccumulatedBlobs() {
    if (this.accumulatedBlobs.length > 0) {
      const combinedBlob = new Blob(this.accumulatedBlobs, { type: 'audio/webm' });
      await this.handleIncomingChunk(combinedBlob);
      this.accumulatedBlobs = [];
    }
  }

  /**
   * Handles uploading a local audio file, processing it, and storing it.
   * This version is corrected for dynamic MIME types and duration calculation.
   */
  uploadRecording(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      if (!(await this.allowedToReplaceAudio())) {
        return reject('User cancelled replacement.');
      }

      this.loadings.fileUploader = true;
      const inputElement = document.createElement('input');
      inputElement.type = 'file';
      inputElement.accept = 'audio/mpeg,audio/webm,audio/wav';
      inputElement.style.display = 'none';

      const cleanup = () => {
        inputElement.removeEventListener('change', onChange);
        inputElement.removeEventListener('cancel', onCancel);
        if (inputElement.parentNode) {
          document.body.removeChild(inputElement);
        }
      };

      const onChange = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        if (!files || files.length === 0) {
          this.loadings.fileUploader = false;
          cleanup();
          return reject('No file selected.');
        }

        const audioFile = files[0];

        try {
          const duration = await getDurationFromFile(audioFile);
          const base64data = await fileToBase64(audioFile);

          await this.storageService.store('contentsCurrentAudio', {
            _id: this.contentID(),
            data: base64data,
            duration: duration,
          });

          if (this.audioRecordingElement) {
            this.audioRecordingElement.nativeElement.src = base64data;
          }
          this.currentAudioFile = audioFile;
          resolve();
        } catch (error) {
          console.error('Error processing uploaded file:', error);
          reject(error);
        } finally {
          this.loadings.fileUploader = false;
          cleanup();
        }
      };

      const onCancel = () => {
        this.loadings.fileUploader = false;
        cleanup();
        reject('File selection was cancelled.');
      };

      inputElement.addEventListener('change', onChange);
      inputElement.addEventListener('cancel', onCancel);

      document.body.appendChild(inputElement);
      inputElement.click();
    });
  }

  uploadAudioAsChunk(): void {
    this.loadings.fileUploader = true;
    const audioDataInfo = this.base64AudioData();
    if (!audioDataInfo) {
      this.loadings.fileUploader = false;
      return;
    }

    const { data: audioData, duration } = audioDataInfo;

    if (!this.params) {
      this.params = {};
    }
    this.params.duration = duration;

    const params = JSON.parse(JSON.stringify(this.params));
    if (this.origin == 'voiceOver') {
      params.type = 'chunk_audio';
    } else {
      params.type = 'audio';
    }

    // Populate all required content and routing parameters for backend association
    if (this.params._id) {
      params.insertBelowID = this.params._id;
    }
    if (!params.contentID) {
      params.contentID = this.contentID();
    }
    if (!params.courseID && this.contentStateService.courseId) {
      params.courseID = this.contentStateService.courseId;
    }
    if (!params.blogID && this.contentStateService.blogId) {
      params.blogID = this.contentStateService.blogId;
    }
    if (!params.noteID && this.contentStateService.noteId) {
      params.noteID = this.contentStateService.noteId;
    }
    if (!params.lecture && this.contentStateService.lecture) {
      params.lecture = this.contentStateService.lecture;
    }
    if (!params.section && this.contentStateService.section) {
      params.section = this.contentStateService.section;
    }
    if (!params.contentName && this.contentStateService.contentName) {
      params.contentName = this.contentStateService.contentName;
    }
    if (!params.contentType && this.contentStateService.contentType) {
      params.contentType = this.contentStateService.contentType;
    }
    delete params._id;

    if (typeof audioData === 'string' && (audioData.startsWith('http') || audioData.startsWith('gs://'))) {
      params.fileLink = audioData.startsWith('http') ? audioData.replace('https://storage.googleapis.com/', 'gs://') : audioData;
      params.origin = 'create-chunk';
      this.chunkService
        .createUploadedChunk(params)
        .pipe(
          tap(async (res: CreateChunkResponse) => {
            const { chunk, content } = res;
            await this.storageService.store('chunks', chunk);
            const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
            this.storageService.store('contents', composedContent);
          }),
          catchError((err) => {
            console.error('Error creating chunk:', err);
            return [];
          })
        )
        .subscribe({
          next: () => {
            this.chunkService.passChunkEditEvent('all');
            this.loadings.fileUploader = false;
          },
          error: (err) => {
            console.error(err);
            this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
              duration: 2000,
            });
            this.loadings.fileUploader = false;
          },
        });

      this.cleanupOrphanedFiles(params.fileLink);
      return;
    }

    const mimeType = audioData instanceof Blob ? audioData.type : audioData.match(/:(.*?);/)?.[1] || 'audio/webm';
    const ext = mimeType === 'audio/wav' ? 'wav' : mimeType === 'audio/mpeg' || mimeType === 'audio/mp3' ? 'mp3' : 'webm';
    const audioFile = this.currentAudioFile || (audioData instanceof Blob ? blobToFile(audioData, `recording.${ext}`) : base64ToFile(audioData, `recording.${ext}`));
    const filename = this.generateRandomFilename(audioFile.name);

    this.uploadService
      .uploadFileAndGetProgress(audioFile, params.type + '/' + filename)
      .then(() => {
        const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
        params.fileLink = `${environment.gsLinkPrefix + activeTenant + '/' + params.type + '/' + filename}`;
        params.origin = 'create-chunk';
        this.chunkService
          .createUploadedChunk(params)
          .pipe(
            tap(async (res: CreateChunkResponse) => {
              const { chunk, content } = res;
              await this.storageService.store('chunks', chunk);
              const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
              this.storageService.store('contents', composedContent);
            }),
            catchError((err) => {
              console.error('Error creating chunk:', err);
              return [];
            })
          )
          .subscribe({
            next: () => {
              this.chunkService.passChunkEditEvent('all');
              this.loadings.fileUploader = false;
            },
            error: (err) => {
              console.error(err);
              this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
                duration: 2000,
              });
              this.loadings.fileUploader = false;
            },
          });
      })
      .catch((err) => {
        this.loadings.fileUploader = false;
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
          duration: 2000,
        });
        console.error(err);
      });
  }

  async deleteAudioRecording(): Promise<void> {
    const response = await firstValueFrom(this.confirm.confirm());
    if (!response) return;

    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    this.currentAudioFile = null;
    this.recordedBlobs = [];
    if (this.audioRecordingElement) {
      this.audioRecordingElement.nativeElement.removeAttribute('src');
      this.audioRecordingElement.nativeElement.load();
    }

    // 1. Delete from GCS if it is a cloud link
    const audioDataInfo = this.base64AudioData();
    if (audioDataInfo && audioDataInfo.data && typeof audioDataInfo.data === 'string' && (audioDataInfo.data.startsWith('http') || audioDataInfo.data.startsWith('gs://'))) {
      try {
        const gsUrl = audioDataInfo.data.startsWith('http') ? audioDataInfo.data.replace('https://storage.googleapis.com/', 'gs://') : audioDataInfo.data;
        const fileRef = ref(this.storage, gsUrl);
        await deleteObject(fileRef);
        console.log('Successfully deleted audio from GCS:', gsUrl);
      } catch (err) {
        console.error('Error deleting audio file from GCS:', err);
      }
    }

    // 2. Delete from local IndexedDB failsafe cache
    try {
      if (this.recordingSessionId) {
        await failsafeDb.chunks.where('sessionId').equals(this.recordingSessionId).delete();
      }
    } catch (err) {
      console.error('Error deleting from failsafeDb:', err);
    }

    // 3. Clean up any remaining orphaned files attached to this component
    await this.cleanupOrphanedFiles();

    // Notify the parent component
    this.audioRecordingDeleted.emit();

    this.storageService.remove('contentsCurrentAudio', [this.contentID()]);
  }

  private async cleanupOrphanedFiles(keepUrl?: string) {
    try {
      const orphans = await failsafeDb.orphanedFiles.where('contentId').equals(this.contentID()).toArray();
      for (const orphan of orphans) {
        if (keepUrl && orphan.url === keepUrl) continue;

        try {
          const fileRef = ref(this.storage, orphan.url);
          await deleteObject(fileRef);
          console.log('Cleaned up orphaned file:', orphan.url);
        } catch (e: any) {
          console.error('Failed to delete orphaned file (might be deleted already):', orphan.url, e);
        }
        await failsafeDb.orphanedFiles.delete(orphan.url);
      }
    } catch (err) {
      console.error('Error during orphaned files cleanup', err);
    }
  }

  generateContentFromAudio() {
    if (!this.canUseAI) return;
    const audioDataInfo = this.base64AudioData();
    if (!audioDataInfo) {
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
      return;
    }

    const { data: audioData } = audioDataInfo;
    let defaultFilename = 'Voice Note.webm';
    let mediaContext: any = {
      type: 'audio',
      filename: this.currentAudioFile?.name || defaultFilename,
    };

    if (typeof audioData === 'string' && (audioData.startsWith('http') || audioData.startsWith('gs://'))) {
      mediaContext.url = audioData;
    } else if (this.currentAudioFile) {
      mediaContext.file = this.currentAudioFile;
      if (this.currentAudioFile.type === 'audio/wav') mediaContext.filename = 'Voice Note.wav';
      else if (this.currentAudioFile.type === 'audio/mpeg' || this.currentAudioFile.type === 'audio/mp3') mediaContext.filename = 'Voice Note.mp3';
    } else if (audioData instanceof Blob) {
      const file = blobToFile(audioData, 'recording.webm');
      mediaContext.file = file;
    } else {
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
      return;
    }

    this.contentStateService.openContentGenerator({
      initialSubject: '',
      mediaContext,
    });
  }

  async onLiveTranscriptionData(eventOrBuffer: any): Promise<void> {
    if (!this.contentStateService.isLiveTranscribing() || this.contentStateService.liveTranscriptionSource() !== 'recorder') {
      return;
    }

    let liveTranscriptionData: LiveTranscriptionPayload = {
      data: null,
      contentID: this.contentID(),
      language: this.translate.currentLang,
      settings: { model: this.transcriptionSettings.model || TranscriptionModels.CHIRP3 },
      tenantId: this.tenantService.activeTenantId() || localStorage.getItem('active_workspace') || undefined,
    };

    if (!eventOrBuffer) {
      console.log('no data received');
      this.socketService.emitEvent('transcribe-data', liveTranscriptionData);
      return;
    }

    if (eventOrBuffer instanceof ArrayBuffer) {
      liveTranscriptionData.data = eventOrBuffer;
    } else if (eventOrBuffer.data && eventOrBuffer.data instanceof Blob) {
      liveTranscriptionData.data = await eventOrBuffer.data.arrayBuffer();
    } else {
      liveTranscriptionData.data = eventOrBuffer;
    }

    this.socketService.emitEvent('transcribe-data', liveTranscriptionData);
  }

  private _getOrCreateChunkForTranscription(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (this.params?._id) {
        this.contentStateService.setHighlightedChunkId(this.params._id);
        resolve(this.params._id);
        return;
      }

      const chunks = this.contentStateService.content();
      const lastChunk = chunks?.[chunks.length - 1];
      const isLastChunkEmptyTranscript = lastChunk && lastChunk.type === 'canvas-transcript' && lastChunk.editions.every((e) => e.content.trim() === '' || e.content === '<p></p>');

      if (isLastChunkEmptyTranscript) {
        this.contentStateService.setHighlightedChunkId(lastChunk._id);
        resolve(lastChunk._id);
      } else {
        const chunkData: any = {
          content: '',
          type: 'canvas-transcript',
          language: this.translate.currentLang,
          contentID: this.contentStateService.contentId,
          courseID: this.contentStateService.courseId,
          blogID: this.contentStateService.blogId,
          noteID: this.contentStateService.noteId,
          lecture: this.contentStateService.lecture,
          section: this.contentStateService.section,
          contentName: this.contentStateService.contentName,
          contentType: this.contentStateService.contentType,
          generateVoice: false,
          generateTranslation: false,
          generateEmbeddings: false,
        };
        this.chunkService.createChunk({ chunks: [chunkData] }).subscribe({
          next: (res: CreateChunkResponse[]) => {
            const newChunk = res[0].chunk;
            this.contentStateService.setHighlightedChunkId(newChunk._id);
            resolve(newChunk._id);
          },
          error: (err) => reject(err),
        });
      }
    });
  }

  private getTranscriptionHtml(finalText: string, interimText?: string): string {
    const lines = finalText ? finalText.split('\n') : [];
    if (interimText) {
      if (lines.length > 0) {
        const lastLine = lines[lines.length - 1];
        lines[lines.length - 1] = lastLine ? `${lastLine} <i>${interimText}</i>` : `<i>${interimText}</i>`;
      } else {
        lines.push(`<i>${interimText}</i>`);
      }
    }
    const htmlLines = lines.map((line) => `<p>${line}</p>`);
    return htmlLines.join('');
  }

  async startLiveTranscription(record = true): Promise<void> {
    if (!this.canUseAI) return;
    this.loadings.transcriber.live = true;
    this.finalTranscription = '';
    this.interimTranscription = '';
    this.isFirstSegmentOfSession = true;
    this.isRecordingAndTranscribing = record;
    const onNotEnoughCreds = () => {
      this.loadings.transcriber.live = false;
      this.toast.snackbar.openFromComponent(NotEnoughCreditComponent, {
        data: {
          message: this.translate.instant('Toastr.notEnoughCredits'),
          linkText: this.translate.instant('Toastr.addCreditsHere'),
          linkUrl: '/myspace/credit',
        },
        duration: 10000,
      });
    };

    try {
      await this.socketService.initSocket();
    } catch (err) {
      console.error('Failed to init socket', err);
      this.loadings.transcriber.live = false;
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
      return;
    }

    try {
      const chunkId = await this._getOrCreateChunkForTranscription();
      this.liveTranscriptionChunkId = chunkId;
      await this.chunkService.setLiveTranscriptionChunkId(chunkId);

      const chunks = this.contentStateService.content();
      const targetChunk = chunks.find((c: any) => c._id === chunkId);
      const edition = targetChunk?.editions?.find((e: any) => e.language === this.translate.currentLang) || targetChunk?.editions?.[0];

      let initialHtml = '';
      if (this.continueTranscriptionMode && this.currentText) {
        initialHtml = this.currentText;
      } else {
        initialHtml = edition?.content || '';
      }

      if (initialHtml) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = initialHtml;
        const paragraphs = tempDiv.querySelectorAll('p');
        if (paragraphs.length > 0) {
          this.finalTranscription = Array.from(paragraphs)
            .map((p) => (p.textContent || p.innerText || '').trim())
            .filter((text) => text.length > 0)
            .join('\n');
        } else {
          this.finalTranscription = (tempDiv.textContent || tempDiv.innerText || '').trim();
        }
      }

      this.contentStateService.openCanvas({
        title: this.translate.instant('Global.Transcript'),
        html: initialHtml,
        source: 'transcript',
        chunkId: chunkId,
        contentId: this.contentID(),
        edition: edition,
      });

      this.contentStateService.openContentGenerator({
        initialSubject: this.translate.instant('Global.Transcript'),
        context: {
          id: chunkId,
          title: this.translate.instant('Global.Transcript'),
          name: this.translate.instant('Global.Transcript'),
          type: 'chunk',
        },
        isAuto: true,
      });

      this.initiateTranscription(onNotEnoughCreds, record);
    } catch (error) {
      console.error('Failed to get or create chunk for transcription', error);
      this.loadings.transcriber.live = false;
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
    }
  }

  private initiateTranscription(onNotEnoughCreds: () => void, record: boolean) {
    this.chunkService.initializeLiveTranscription({}).subscribe({
      next: async (output) => {
        if (!output) {
          onNotEnoughCreds();
          return;
        }
        this.contentStateService.setLiveTranscriptionState(true, 'recorder');
        this.chunkService.setLiveTranscriptionStartedValue(true);
        this.currentRecordedBlobs = [];
        this.recordingSessionId = uuidv4();
        this.chunkIndex = 0;
        this.accumulatedBlobs = [];
        this.socketService.emitEvent('start-stream', {});

        try {
          let audioConstraints: any = { echoCancellation: true };
          const savedMic = localStorage.getItem('selectedMicrophone') || this.selectedMicrophone;
          if (savedMic && savedMic !== 'default') {
            audioConstraints.deviceId = { exact: savedMic };
          }
          
          let getMediaPromise: Promise<MediaStream>;
          try {
            getMediaPromise = (navigator.mediaDevices as any).getUserMedia({ audio: audioConstraints });
          } catch (err) {
             // Synchronous catch (unlikely)
             audioConstraints = { echoCancellation: true };
             if (savedMic && savedMic !== 'default') audioConstraints.deviceId = { ideal: savedMic };
             getMediaPromise = (navigator.mediaDevices as any).getUserMedia({ audio: audioConstraints });
          }
          
          getMediaPromise.catch(async (err) => {
            console.warn('[AudioRecorder] Failed exact microphone, falling back to ideal/default:', err);
            audioConstraints = { echoCancellation: true };
            if (savedMic && savedMic !== 'default') {
              audioConstraints.deviceId = { ideal: savedMic };
            }
            return (navigator.mediaDevices as any).getUserMedia({ audio: audioConstraints });
          })
            .then((media: MediaStream) => {
              if (!this.contentStateService.isLiveTranscribing() || this.contentStateService.liveTranscriptionSource() !== 'recorder') {
                media.getTracks().forEach((track) => track.stop());
                return;
              }
              checkAudioStreamActivity(media).then((hasActivity) => {
                if (!hasActivity) {
                  this.toast.showAudioTroubleshootingDialog();
                }
              });
              this.contentStateService.liveAudioStream.set(media);
              this.audioFeed = media;

              // Setup AudioContext at 16000Hz to handle automatic resampling
              const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
              if (AudioContextClass) {
                const audioCtx = new AudioContextClass({ sampleRate: 16000 });
                this.transcriptionAudioContext = audioCtx;

                const micSource = audioCtx.createMediaStreamSource(media);
                this.transcriptionMicSource = micSource;

                const workletCode = `
                  class DictationProcessor extends AudioWorkletProcessor {
                    process(inputs, outputs, parameters) {
                      const input = inputs[0];
                      if (input && input.length > 0) {
                        const float32Data = input[0];
                        const int16Buffer = new Int16Array(float32Data.length);
                        for (let i = 0; i < float32Data.length; i++) {
                          const s = Math.max(-1, Math.min(1, float32Data[i]));
                          int16Buffer[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
                        }
                        this.port.postMessage(int16Buffer.buffer, [int16Buffer.buffer]);
                      }
                      return true;
                    }
                  }
                  registerProcessor('dictation-processor', DictationProcessor);
                `;
                const blob = new Blob([workletCode], { type: 'application/javascript' });
                const workletUrl = URL.createObjectURL(blob);
                
                audioCtx.audioWorklet.addModule(workletUrl).then(() => {
                  const workletNode = new AudioWorkletNode(audioCtx, 'dictation-processor');
                  this.transcriptionWorkletNode = workletNode;

                  micSource.connect(workletNode);
                  workletNode.connect(audioCtx.destination);

                  workletNode.port.onmessage = (event: MessageEvent) => {
                    this.onLiveTranscriptionData(event.data);
                  };
                }).catch(err => console.error('Failed to load AudioWorklet module:', err));

                if (audioCtx.state === 'suspended') {
                  audioCtx.resume();
                }
              }

              if (record) {
                this.mediaRecorder = new MediaRecorder(media, {
                  mimeType: 'audio/webm',
                });
                this.mediaRecorder.start(500);
                this.mediaRecorder.ondataavailable = (event) => {
                  if (event.data && event.data.size > 0) {
                    this.handleLiveTranscriptionRecordingChunk(event.data);
                  }
                };
              }
              this.loadings.transcriber.live = false;
            })
            .catch((err: any) => {
              console.error('Error starting live transcription microphone:', err);
              this.loadings.transcriber.live = false;
              this.contentStateService.setLiveTranscriptionState(false);
              this.chunkService.setLiveTranscriptionStartedValue(false);

              if (err.name === 'NotAllowedError' || err.message?.includes('Permission denied')) {
                this.toast.showDevicePermissionError();
              } else {
                this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'An error occurred', 'OK', { duration: 3000 });
              }
            });
        } catch (err) {
          console.error(err);
          this.loadings.transcriber.live = false;
          this.contentStateService.setLiveTranscriptionState(false);
          this.chunkService.setLiveTranscriptionStartedValue(false);
        }
      },
      error: (err) => {
        onNotEnoughCreds();
        this.loadings.transcriber.live = false;
        console.error(err);
      },
    });
  }

  stopLiveTranscription(): void {
    if (!this.contentStateService.isLiveTranscribing() || this.contentStateService.liveTranscriptionSource() !== 'recorder') {
      return;
    }

    // Set states immediately to prevent recursion from synchronous event emissions
    this.chunkService.setLiveTranscriptionStartedValue(false);
    this.contentStateService.setLiveTranscriptionState(false);
    this.loadings.transcriber.live = false;

    // 1. Clean up the Web Audio API transcription pipeline FIRST
    //    Chrome keeps the mic indicator active as long as any AudioContext holds
    //    a MediaStreamAudioSourceNode. We must disconnect and close them BEFORE
    //    stopping the underlying tracks.
    if (this.transcriptionWorkletNode) {
      try {
        this.transcriptionWorkletNode.port.onmessage = null;
        this.transcriptionWorkletNode.disconnect();
      } catch (e) {
        console.error('Error disconnecting transcriptionWorkletNode:', e);
      }
      this.transcriptionWorkletNode = null;
    }
    if (this.transcriptionMicSource) {
      try {
        this.transcriptionMicSource.disconnect();
      } catch (e) {
        console.error('Error disconnecting transcriptionMicSource:', e);
      }
      this.transcriptionMicSource = null;
    }
    if (this.transcriptionAudioContext) {
      try {
        this.transcriptionAudioContext.close();
      } catch (e) {
        console.error('Error closing transcriptionAudioContext:', e);
      }
      this.transcriptionAudioContext = null;
    }

    // 2. Now release tracks — after all AudioContexts referencing the stream are closed
    //    Also read from the signal as a fallback in case this.audioFeed was already cleared
    const streamToRelease = this.audioFeed || this.contentStateService.liveAudioStream();
    if (streamToRelease) {
      streamToRelease.getTracks().forEach((track) => track.stop());
    }
    this.audioFeed = null;
    this.contentStateService.liveAudioStream.set(null);

    const liveTranscriptionData: LiveTranscriptionPayload = {
      data: null,
      contentID: this.contentID(),
      language: this.translate.currentLang,
      settings: { model: this.transcriptionSettings.model || TranscriptionModels.CHIRP3 },
    };

    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.onstop = async () => {
        this.socketService.emitEvent('stream-end', liveTranscriptionData);
        if (this.isRecordingAndTranscribing) {
          await this.flushAccumulatedBlobs();
          this.processFinishedRecording();
        }
      };
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
    } else {
      this.mediaRecorder = null;
      this.socketService.emitEvent('stream-end', liveTranscriptionData);
      this.contentStateService.triggerStopLiveTranscription();
    }

    // Finalize chunk status on stop to remove the loading spinner
    const chunkId = this.liveTranscriptionChunkId || this.contentStateService.highlightedChunkId();
    if (chunkId) {
      const chunks = this.contentStateService.content();
      const targetChunk = chunks.find((c: any) => c._id === chunkId);
      if (targetChunk) {
        const edition = targetChunk.editions?.find((e: any) => e.language === this.translate.currentLang) || targetChunk.editions?.[0];
        if (edition) {
          this.chunkService
            .editChunk({
              id: chunkId,
              edition: edition,
              type: 'canvas-transcript',
              language: this.translate.currentLang,
              content: this.getTranscriptionHtml(this.finalTranscription),
              contentID: this.contentStateService.contentId,
              status: 'active',
            })
            .subscribe({
              next: () => {
                targetChunk.status = 'active';
                this.contentStateService.updateChunkLocally(chunkId, targetChunk);
              },
              error: (err) => console.error('Error updating live chunk status to active:', err),
            });
        }
      }
    }
  }

  async transcribeAudio(): Promise<void> {
    if (!this.canUseAI) return;
    const audioDataInfo = this.base64AudioData();
    if (!audioDataInfo) {
      console.warn('Transcription cancelled: No audio data available.');
      return;
    }

    const { data: audioData, duration } = audioDataInfo;
    const isCloudLink = typeof audioData === 'string' && (audioData.startsWith('http') || audioData.startsWith('gs://'));

    if (!isCloudLink && !this.currentAudioFile && !(audioData instanceof Blob)) {
      console.warn('Transcription cancelled: No audio file or Blob available.');
      return;
    }

    this.transcriptionOptionsSub?.unsubscribe();
    const triggerElement = this.audioPlayerComponent.optionsButtonRef;
    const { ref: overlayRef, componentInstance } = this.dynamicOverlayService.open((triggerElement as any)._elementRef, TranscriptionOptionsComponent, { data: {} });
    this.transcriptionOptionsSub = componentInstance.commandSelected.subscribe(async (command: { type: CommandType }) => {
      if (command.type === 'start') {
        this.dynamicOverlayService.close();
        this.loadings.transcriber.recorded = true;
        this.cdr.detectChanges();
        this.contentStateService.setLoading({ status: 'loading', message: this.translate.instant('Content.Chunk.TranscriptionInProgress'), reason: 'transcribe-audio' });

        try {
          const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
          let fileLink = '';

          if (isCloudLink) {
            fileLink = audioData.startsWith('http') ? audioData.replace('https://storage.googleapis.com/', 'gs://') : audioData;
          } else {
            const audioFile = this.currentAudioFile || (audioData instanceof Blob ? blobToFile(audioData, 'recording.webm') : base64ToFile(audioData, 'recording.webm'));
            const filename = this.generateRandomFilename(audioFile.name);
            const storagePath = `audios_to_transcribe/${filename}`;
            await this.uploadService.uploadFileAndGetProgress(audioFile, storagePath);
            fileLink = `${environment.gsLinkPrefix}${activeTenant}/${storagePath}`;
          }

          const chunkId = await this._getOrCreateChunkForTranscription();
          await this.chunkService.setLiveTranscriptionChunkId(chunkId);

          const transcriptionData: TranscriptionPayload = {
            chunkId,
            contentID: this.contentID(),
            fileLink: fileLink,
            language: this.translate.currentLang,
            settings: { model: this.transcriptionSettings.model || TranscriptionModels.CHIRP3 },
            duration: duration || this.params?.duration,
          };

          const chunks = this.contentStateService.content();
          const targetChunk = chunks.find((c: any) => c._id === chunkId);
          const edition = targetChunk?.editions?.find((e: any) => e.language === this.translate.currentLang) || targetChunk?.editions?.[0];

          if (targetChunk) {
            targetChunk.status = 'processing';
            this.contentStateService.updateChunkLocally(chunkId, targetChunk);
          }

          await this.chunkService.transcribeAudio(transcriptionData);

          if (targetChunk) {
            this.contentStateService.openCanvas({
              title: this.translate.instant('Global.Transcript'),
              html: `<p><i>${this.translate.instant('Content.Chunk.TranscriptionInProgress')}</i></p>`,
              source: 'transcript',
              chunkId: chunkId,
              contentId: this.contentID(),
              edition: edition,
            });

            this.contentStateService.openContentGenerator({
              initialSubject: this.translate.instant('Global.Transcript'),
              context: {
                id: chunkId,
                title: this.translate.instant('Global.Transcript'),
                name: this.translate.instant('Global.Transcript'),
                type: 'chunk',
              },
              isAuto: true,
            });
          }

          this.contentStateService.setLoading({
            status: 'success',
            message: this.translate.instant('Content.Chunk.TranscriptionStarted') || 'Transcription started in background',
            reason: 'transcribe-audio',
          });
        } catch (err) {
          console.error('An error occurred during the transcription process:', err);
          this.contentStateService.setLoading({ status: 'error', message: this.translate.instant('Content.Chunk.TranscriptionFailed'), reason: 'transcribe-audio' });
        } finally {
          this.loadings.transcriber.recorded = false;
          this.cdr.detectChanges();
          setTimeout(() => this.contentStateService.setLoading({ status: 'idle' }), 2000);
        }
      } else {
        this.dynamicOverlayService.close();
      }
    });

    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => this.transcriptionOptionsSub?.unsubscribe());
  }

  async getDownloadURL(url: string): Promise<string> {
    if (!url) return '';
    try {
      const gsUrl = url.replace('https://storage.googleapis.com/', 'gs://');
      const reference = ref(this.storage, gsUrl);
      return await getDownloadURL(reference);
    } catch (err) {
      console.error('Error getting download URL:', err);
      try {
        let filename = url.split(environment.publicBucketStorage)[1];
        const fallbackGsUrl = `gs://${environment.publicBucketStorage}${filename}`;
        const storageRef = ref(this.storage, fallbackGsUrl);
        return await getDownloadURL(storageRef);
      } catch (fallbackErr) {
        console.error('Fallback error getting download URL:', fallbackErr);
        return url;
      }
    }
  }

  private generateRandomFilename(originalName: string): string {
    const extension = originalName.substring(originalName.lastIndexOf('.'));
    const randomPart = Math.random().toString(36).substring(2, 15);
    return `${randomPart}${extension}`;
  }

  ngOnDestroy(): void {
    this.stopRecordingAudio();
    this.stopLiveTranscription();
    this.userConfiguration?.unsubscribe();
    if (this.audioFeed) {
      this.audioFeed.getTracks().forEach((track) => track.stop());
      this.audioFeed = null;
    }
    this.socketSubscription?.unsubscribe();
    this.socketService.closeSocket();
    this.TTSFileSubscription?.unsubscribe();
    this.chunkCreatedSubscription?.unsubscribe();

    // Prevent memory leaks by revoking created blob URLs
    this.objectUrlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrlsToRevoke = [];
  }

  private createSafeObjectURL(blob: Blob | File): string {
    const url = URL.createObjectURL(blob);
    this.objectUrlsToRevoke.push(url);
    return url;
  }
}
